import React, { useState, useEffect, useCallback } from 'react';

interface Props {
  text: string;
  approvalRequired: boolean;
  tripId: number;
  isNewlyCompleted: boolean;
}

const COUNTDOWN_SECONDS = 5;

const ArrangementCompletedMessage = ({ text, approvalRequired, tripId, isNewlyCompleted }: Props) => {
  const styles = getStyles();
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);

  const handleRedirect = useCallback(() => {
    window.location.href = `/trips/${tripId}/messages`;
  }, [tripId]);

  useEffect(() => {
    if (!isNewlyCompleted) {
      return undefined;
    }

    if (countdown <= 0) {
      handleRedirect();
      return undefined;
    }

    const timer = setTimeout(() => {
      setCountdown(prev => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown, handleRedirect, isNewlyCompleted]);

  const renderRedirectSection = () => {
    if (isNewlyCompleted) {
      return (
        <div style={styles.redirectNote}>
          {countdown > 0 ? `${countdown}秒後にオペレーター画面に遷移します...` : '遷移中...'}
        </div>
      );
    }

    return (
      <button type="button" style={styles.redirectButton} onClick={handleRedirect}>
        オペレーター画面へ →
      </button>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.iconWrapper}>
        <span style={styles.checkIcon}>✓</span>
      </div>
      <div style={styles.messageText}>{text}</div>
      {approvalRequired && <div style={styles.approvalNote}>承認フローが完了次第、手配を進めます。</div>}
      {renderRedirectSection()}
    </div>
  );
};

const getStyles = () => ({
  container: {
    backgroundColor: '#e8f5e9',
    border: '1px solid #81c784',
    borderRadius: '8px',
    padding: '16px',
    display: 'flex' as const,
    flexDirection: 'column' as const,
    alignItems: 'center' as const,
    gap: '12px',
    maxWidth: '480px'
  },
  iconWrapper: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: '#4caf50',
    display: 'flex' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const
  },
  checkIcon: {
    color: '#ffffff',
    fontSize: '20px',
    fontWeight: 'bold' as const
  },
  messageText: {
    fontSize: '14px',
    color: '#333',
    lineHeight: '1.5',
    textAlign: 'center' as const
  },
  approvalNote: {
    fontSize: '12px',
    color: '#666',
    lineHeight: '1.5',
    textAlign: 'center' as const,
    padding: '8px 12px',
    backgroundColor: '#fff3e0',
    borderRadius: '4px',
    border: '1px solid #ffcc80'
  },
  redirectNote: {
    fontSize: '13px',
    color: '#1976d2',
    lineHeight: '1.5',
    textAlign: 'center' as const,
    padding: '8px 12px',
    backgroundColor: '#e3f2fd',
    borderRadius: '4px',
    border: '1px solid #90caf9',
    fontWeight: 500 as const
  },
  redirectButton: {
    fontSize: '13px',
    color: '#ffffff',
    backgroundColor: '#1976d2',
    border: 'none',
    borderRadius: '4px',
    padding: '10px 20px',
    cursor: 'pointer',
    fontWeight: 500 as const
  }
});

export default ArrangementCompletedMessage;
